import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment-timezone';
import ActionsDropdown from './ActionsDropdown';
import Thumbnail from './Thumbnail';
import { IImage } from './interfaces';

const FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB'];

const formatFileSize = (bytes: number | null): string | null => {
  if (bytes === null || bytes === undefined) return null;

  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < FILE_SIZE_UNITS.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${FILE_SIZE_UNITS[unitIndex]}`;
};

interface UseImageTableColumnsProps {
  onEditAction: (image: IImage) => void;
}

const useImageTableColumns = ({ onEditAction }: UseImageTableColumnsProps) => {
  const columnDefs = useMemo(() => (
    [
      {
        createdCell: (td, _cellData, rowData) => {
          const isMissing = (src: string) => !src || src === '/missing.png';
          const thumbnailUrl = isMissing(rowData.thumb_url) ? rowData.es_thumb_url : rowData.thumb_url;
          const url = isMissing(rowData.url) ? rowData.es_url : rowData.url;
          ReactDOM.render(<Thumbnail thumbnailUrl={thumbnailUrl} url={url} />, td);
        },
        name: 'thumb_url',
        targets: 0,
        title: 'Image',
      },
      {
        createdCell: (td, _cellData, rowData) => {
          ReactDOM.render(
            <>{rowData.image_en_file_name || rowData.image_es_file_name || '—'}</>,
            td,
          );
        },
        name: 'image_en_file_name',
        targets: 1,
        title: 'File Name',
        width: '30%',
        class: 'tw-align-middle',
      },
      {
        class: 'tw-align-middle',
        name: 'id',
        targets: 2,
        title: 'Image ID',
      },
      {
        createdCell: (td, cellData, rowData) => {
          const size = cellData || formatFileSize(rowData.image_es_file_size);
          ReactDOM.render(<>{size || '—'}</>, td);
        },
        name: 'image_file_size',
        targets: 3,
        title: 'File Size',
        type: 'file-size',
        class: 'tw-align-middle',
      },
      {
        createdCell: (td, _cellData, rowData) => {
          const date = rowData.image_en_updated_at || rowData.image_es_updated_at;
          ReactDOM.render(<>{date ? moment(date).format('MMM D, YYYY') : '—'}</>, td);
        },
        name: 'image_en_updated_at',
        targets: 4,
        title: 'Date Uploaded',
        class: 'tw-align-middle',
      },
      {
        name: 'num_of_uses',
        targets: 5,
        title: 'Num of Uses',
        width: '7%',
        class: 'tw-align-middle',
      },
      {
        createdCell: (td, cellData, rowData) => {
          ReactDOM.render(
            <ActionsDropdown
              rowData={rowData}
              openEditImageModal={onEditAction}
            />,
            td,
          );
        },
        class: 'tw-align-middle',
        name: 'actions',
        searchable: false,
        sortable: false,
        targets: 6,
      },
    ]
  ), []);

  const columns = useMemo(() => columnDefs.map(c => c.name), [columnDefs]);

  const columnMapping = useMemo(() => columnDefs.map(c => (c.name === 'actions' ? null : c.name)), [columnDefs]);

  return { columns, columnDefs, columnMapping };
};

export default useImageTableColumns;
